/**
 * Phase 5 batch 3 Slice 7 — privacy page.
 *
 * Documents the practices the architecture already enforces. The page
 * is reachable from the AuthGate sign-in footer and from the
 * SignedInBadge's "Privacy" link. We deliberately keep this terse and
 * factual — no marketing copy, no compliance promises we haven't
 * made — per the Phase 5 plan's "Compliance" decision.
 */
export interface IPrivacyPageProps {
  onClose: () => void;
}

export function PrivacyPage({ onClose }: IPrivacyPageProps): JSX.Element {
  return (
    <main
      style={{
        maxWidth: '40rem',
        margin: '4rem auto',
        padding: '0 1.5rem',
        fontFamily:
          'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        color: '#1a2233',
        lineHeight: 1.55,
      }}
    >
      <button
        type="button"
        onClick={onClose}
        style={{
          background: 'none',
          border: 0,
          color: '#2563eb',
          cursor: 'pointer',
          padding: 0,
          fontSize: '0.95rem',
          marginBottom: '1.5rem',
        }}
      >
        ← Back
      </button>
      <h1>Privacy practices</h1>
      <p style={{ color: '#54627a', marginTop: '-0.5rem' }}>
        Last updated 2026-04-24.
      </p>

      <h2>What data we store</h2>
      <p>
        Your application answers, the documents you upload, and a
        per-application audit trail of changes. Nothing else. We do
        not have analytics, advertising trackers, or third-party
        integrations.
      </p>

      <h2>Where it lives</h2>
      <ul>
        <li>
          <strong>Answers</strong> are stored in Firestore under a
          single document scoped to your user account.
        </li>
        <li>
          <strong>Documents you upload</strong> live in Cloud Storage
          under a path prefixed by your user id; a metadata record is
          kept in Firestore alongside the application.
        </li>
        <li>
          <strong>Audit log</strong> entries are append-only — even
          you cannot rewrite or delete them through the app. PII
          fields (SSN, A-Number, dates of birth, etc.) are redacted
          before they reach the audit log.
        </li>
      </ul>

      <h2>Who can read it</h2>
      <p>
        Only you. Firebase security rules block any other user account
        from reading your application, your documents, or your audit
        trail. The founders can inspect data via the Firebase Console
        when troubleshooting (which bypasses application-level rules
        but is logged by Google IAM); we will only do so with your
        knowledge.
      </p>

      <h2>How filled PDFs are produced</h2>
      <p>
        USCIS form PDFs are filled in your browser, never on a
        server. Your sensitive answers do not transit our
        infrastructure beyond the per-user Firestore document they
        live in.
      </p>

      <h2>Account deletion</h2>
      <p>
        Email <a href="mailto:hello@greenbroad.co">hello@greenbroad.co</a>
        {' '}and we will purge your account, application, documents, and
        audit log from our systems. We will keep nothing.
      </p>

      <h2>What we do not promise</h2>
      <p>
        We are not HIPAA-certified and we do not have a SOC 2 Type 2
        report. If your case requires either, this is not the right
        product for you yet.
      </p>

      <h2>Changes to this page</h2>
      <p>
        Material changes get an email notice to the address on your
        account before they take effect.
      </p>

      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: '2rem',
          padding: '0.6rem 1.25rem',
          background: '#2563eb',
          color: '#fff',
          border: 0,
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '0.95rem',
        }}
      >
        Back
      </button>
    </main>
  );
}
